import React from "react";
import { Grid, Paper, Typography } from "@mui/material";

import calendar from "../../../assets/Icons/calendar.png";
import co2 from "../../../assets/Icons/co2.png";
import { Constants } from "constants/constant";

interface BaselineYearAndTotalProps {
  totalEmissions: number;
  baseYear?: number;
  displayContext?: "portfolio" | "location";
}

/**
 * A React functional component that displays the baseline year and total emissions in a styled card format.
 *
 * @component
 * @param {Object} props - Props passed to the component.
 * @param {number} props.totalEmissions - The total emissions value to be displayed.
 * @param {number} props.baseYear - The baseline year to be displayed.
 */
const BaselineYearAndTotal: React.FC<BaselineYearAndTotalProps> = ({
  totalEmissions,
  baseYear,
  displayContext = "portfolio",
}) => {
  const UNIT_TCO2E = Constants.UNIT_TCO2E;
  const isLocationView = displayContext === "location";
  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        border: "1px solid #fecbc0",
        borderRadius: "12px",
        backgroundColor: "#fef5f3",
        p: isLocationView ? 1.35 : 2.5,
      }}
    >
      <Typography
        sx={{
          color: "#b45a3f",
          fontWeight: 590,
          fontSize: "0.78rem",
          mb: isLocationView ? 0.9 : 2,
        }}
      >
        Baseline Year & Total Emissions ({UNIT_TCO2E})
      </Typography>
      <Grid container spacing={isLocationView ? 1.1 : 2}>
        <Grid item xs={6}>
          <EmissionCard
            title={baseYear && baseYear > 0 ? baseYear : "N/A"}
            subtitle="Baseline Year"
            imageSrc={calendar}
            isLocationView={isLocationView}
            testId="baselineyear"
          />
        </Grid>
        <Grid item xs={6}>
          <EmissionCard
            title={totalEmissions.toLocaleString("en-IN", {
              maximumFractionDigits: 2,
            })}
            subtitle="Total Emissions"
            imageSrc={co2}
            isLocationView={isLocationView}
            testId="totalemission"
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

/**
 * A reusable card component to display emission-related data.
 *
 * @component
 * @param {Object} props - Props passed to the component.
 * @param {string | number} props.title - The title to be displayed in the card.
 * @param {string} props.subtitle - The subtitle to be displayed in the card.
 * @param {string} props.imageSrc - The source of the image to be displayed.
 */
const EmissionCard: React.FC<{
  title: string | number;
  subtitle: string;
  imageSrc: string;
  isLocationView?: boolean;
  testId?: string;
}> = ({ title, subtitle, imageSrc, isLocationView = false, testId }) => {
  const subtitleFontSize = isLocationView ? "0.66rem" : "0.7rem";

  return (
    <Paper
      elevation={0}
      sx={{
        p: isLocationView ? 1 : 2,
        borderRadius: "8px",
        border: "1px solid #fecbc0",
        backgroundColor: "#fff",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        position: "relative",
        minHeight: isLocationView ? 60 : 90,
      }}
    >
      <Typography
        data-testid={testId}
        sx={{
          fontSize: isLocationView ? "1.05rem" : "1.3rem",
          fontWeight: 700,
          color: "#0f2b52",
          mb: isLocationView ? 0.2 : 0.5,
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          color: "#64748b",
          fontSize: subtitleFontSize,
        }}
      >
        {subtitle}
      </Typography>
      <img
        src={imageSrc}
        alt={subtitle}
        style={{
          width: isLocationView ? "26px" : "34px",
          height: isLocationView ? "26px" : "34px",
          opacity: 0.4,
          filter: "blur(0.8px)",
          position: "absolute",
          bottom: isLocationView ? 6 : 12,
          right: isLocationView ? 6 : 12,
        }}
      />
    </Paper>
  );
};

export default BaselineYearAndTotal;
